import styles from './TenantUser.module.css';
import '../../resources/css/admin_style.css';
import React, { useContext, useEffect, useState } from "react";
import { ModalContext } from "../../utils/modal/ModalProvider";
import { AuthContext } from "../../utils/contextapi/AuthProvider";
import { IAM_SERVER_TENANT_USER } from "../../utils/Config";
import axios from "../../utils/api/axios";
import { TableContext, TableStateType } from '../../utils/contextapi/TableProvider';
import { useTenantList } from '../../utils/service/tenantList';
import { DialogEnum, showDialog } from '../../utils/dialog/Dialog';
import DetailModal from '../../utils/modal/DetailModal';
import { TenantUserInviteModal } from './modal/TenantUserInviteModal';
import TenantsUserTable from './component/TenantsUserTable';
import { FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa'
import { TenantUserDetailModal } from './modal/TenantUserDetailModal';
import { getCookie } from '../../utils/cookies/cookies';
import { InnocodeList, InnocodeButton, useInnocodeLoadingOverlay } from '@intsoft/innocode-core';

export const ModalEnum = {
    //기기 관리 Modal Enum
    REGISTER: 'register_one_button',
    DETAIL: 'modify_and_delete_two_button'
}

/**
 * 테넌트 사용자 관리 화면
 * @param isMobile
 * @param setMovePage
 * @returns {JSX.Element}
 * @constructor
 */
function TenantUser({ isMobile, setMovePage }) {
    const emptyDateRange = [null, null];
    const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();
    const { openModal } = useContext(ModalContext);
    const [isMobileWindow, setIsMobileWindow] = useState(false);
    const context = useContext(TableContext);
    const { user: authenticatedUser } = useContext(AuthContext);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileWindow(window.innerWidth < 768); // 모바일 기준 너비
        };
        handleResize(); // 초기값
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    //로그인된 사용자의 테넌트 리스트
    const { tenantList, isLoading: isTenantListLoading } = useTenantList(); // isLoading 상태도 함께 가져옴

    //로그인 사용자 테넌트 정보
    const [currentUser, setCurrentUser] = useState(null);

    //검색 조건
    const [searchType, setSearchType] = useState('');
    const [searchValue, setSearchValue] = useState('');

    //테이블 데이터
    const [cellData, setCellData] = useState([]);
    const [page, setPage] = useState(0);
    const listSize = 10; // 한 페이지당 보여줄 항목 수
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        context.actions.contextDispatch({
            type: TableStateType.DATE_RANGE,
            data: emptyDateRange
        });
    }, []);

    useEffect(() => {
        if (!isTenantListLoading && tenantList.length > 0) {
            const transformed = transformToTreeData(tenantList);
            setTreeData(transformed);

            // 페이지 로드 시 또는 tenantList 변경 시 첫 번째 테넌트를 자동으로 선택하고 싶다면:
            if (!selectedGroup && transformed.length > 0) {
                const firstGroup = transformed[0];
                setSelectedGroup(firstGroup);
                getTenantUsersAll(firstGroup.key, 0, listSize, '', ''); // 첫 번째 그룹의 사용자 목록 조회
            }
            // 만약 기존에 선택된 그룹이 있었다면, 그 그룹의 정보를 유지하면서 사용자 목록을 다시 불러올 수 있습니다.
            else if (selectedGroup) {
                getTenantUsersAll(selectedGroup.key, page, listSize, '', '');
            }
        }
    }, [tenantList, isTenantListLoading]); // tenantList 또는 로딩 상태가 변경될 때 실행

    const transformToTreeData = (data) => {
        return data.map(item => ({
            title: item.tenantName,
            key: item.tenantId,
            tenantId: item.tenantId,
            tenantName: item.tenantName,
            currentUserPermission: item.currentUserPermission,
        }));
    };

    //---- 그룹트리 ----//
    const [showGroupTree, setShowGroupTree] = useState(true);
    const [treeData, setTreeData] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);

    const selectedTenant = tenantList.find((tenant) => tenant.tenantId === selectedGroup?.key) || null;

    const currentTenantType = String(
        selectedTenant?.currentUserPermission?.tenantType || selectedTenant?.tenantType || currentUser?.tenantType || ''
    ).toLowerCase();
    const canManageTenantUser = currentTenantType === 'owner' || currentTenantType === 'manager';

    const getTenantUsersAll = async (tenantId, page = 0, size = listSize, searchType = '', searchValue = '') => {
        showLoading();
        try {
            const url = IAM_SERVER_TENANT_USER + "/all";

            // dateRange 가져오기 (context로부터 최신 상태를 반영)
            const [start, end] = context.state.dateRange;

            const conditions = [
                { field: "tenantId", operator: "EQUALS", value: tenantId }
            ];

            if (start && end) {
                conditions.unshift(
                    { field: "createdDate", operator: "GREATER_THAN_OR_EQUAL", value: new Date(start).getTime() },
                    { field: "createdDate", operator: "LESS_THAN_OR_EQUAL", value: new Date(end).getTime() }
                );
            }

            // 선택 조건: 검색 조건 있을 때만 추가
            if (searchType.trim() && searchValue.trim()) {

                conditions.push({
                    field: searchType,
                    operator: "LIKE",
                    value: searchValue
                });
            }

            // 최종 params 구성
            const params = {
                page: page,
                size: listSize,
                sortField: ['createdDate'],
                sortOrder: ['desc'],
                operator: "AND",
                conditions
            };

            const res = await axios.post(url, params);

            const { data, recordsTotal } = res.data.data;

            const profileIds = data.map(item => item.profileId);

            const tenantUserUrl = IAM_SERVER_TENANT_USER + "/get/tenant";
            const tenantUserParams = {
                tenantId: tenantId,
                profileIds: profileIds
            };

            const tenantUserRes = await axios.post(tenantUserUrl, tenantUserParams);

            // 두 데이터
            const mainList = data; // 원본 테넌트 유저 리스트
            const tenantUserExtraList = tenantUserRes.data.data; // 확장 정보

            // profileId 기준으로 병합
            const mergedData = mainList.map(item => {
                const extra = tenantUserExtraList.find(extraItem => extraItem.profileId === item.profileId);
                return {
                    ...item,
                    ...(extra || {}) // 확장 정보가 있다면 병합, 없으면 무시
                };
            });

            setTotalCount(recordsTotal);
            setCellData(mergedData);


            // 현재 유저 찾기
            const currentUserUrl = `${IAM_SERVER_TENANT_USER}/listByTenant/${tenantId}`;
            const currentUserRes = await axios.get(currentUserUrl);
            const currentUserId = getCookie("user_id");
            const allUsers = currentUserRes.data.data || [];
            const matchedCurrentUser = allUsers.find(user => user.unifiedProfileId === currentUserId);
            setCurrentUser(matchedCurrentUser || null);


        } catch (error) {
            console.error(error);
        } finally {
            hideLoading();
        }
    };

    // 그룹 선택 핸들러 (InnocodeList용으로 간소화)
    const handleGroupSelect = (group) => {
        setPage(0);
        setCurrentUser(null);

        context.actions.contextDispatch({
            type: TableStateType.DATE_RANGE,
            data: emptyDateRange
        });

        setSelectedGroup(group);
        setSearchValue('');

        getTenantUsersAll(group.key, 0, listSize, '', '');
    };


    //검색 조회
    const handleSearch = async (searchType, value) => {
        if (!selectedGroup) return; // null 체크 추가
        setPage(0);
        setSearchType(searchType);
        setSearchValue(value);
        getTenantUsersAll(selectedGroup.key, 0, listSize, searchType, value);
    }

    const handleNextPage = (newPage) => {
        setPage((prevPage) => {
            const newPage = prevPage + 1;
            return newPage;
        });

        getTenantUsersAll(selectedGroup.key, newPage, listSize, searchType, searchValue);
    };

    const handlePrevPage = (newPage) => {
        setPage((prevPage) => {
            const newPage = Math.max(prevPage - 1, 0);
            return newPage;
        });
        getTenantUsersAll(selectedGroup.key, newPage, listSize, searchType, searchValue);
    };

    // 유저 초대 모달 핸들러
    const handleRegisterUser = () => {
        if (!canManageTenantUser) {
            showDialog(DialogEnum.DEFAULT, "권한 없음", "초대 권한은 오너와 관리자만 사용할 수 있습니다.");
            return;
        }

        if (!selectedGroup?.key) {
            showDialog(DialogEnum.DEFAULT, "확인", "테넌트를 먼저 선택 해주세요.");
            return;
        }

        const inviterName = currentUser?.profileName || authenticatedUser?.name;
        if (!inviterName) {
            showDialog(DialogEnum.DEFAULT, "초대 실패", "현재 사용자 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
            return;
        }

        const modalContent = (
            <TenantUserInviteModal
                tenantId={selectedGroup.key}
                inviterName={inviterName}
                canInvite={canManageTenantUser}
            // onSuccess={fetchData}
            />
        );

        const modalOption = { modalTitle: '유저 초대' };
        openModal(<DetailModal content={modalContent} option={modalOption} />);
    }

    // 사용자 삭제 모달 핸들러
    const handleWithdrawal = (id) => {
        if (!canManageTenantUser) {
            showDialog(DialogEnum.DEFAULT, "권한 없음", "탈퇴 권한은 오너와 관리자만 사용할 수 있습니다.");
            return;
        }

        const confirmText = "테넌트 유저를 탈퇴 합니다.";

        showDialog(DialogEnum.CONFIRM, "테넌트 유저 탈퇴 확인", confirmText, () => {
            const url = `${IAM_SERVER_TENANT_USER}/delete/${id}`;

            axios.post(url)
                .then((res) => {
                    showDialog(DialogEnum.SUCCESS, "완료", "테넌트 유저를 탈퇴 완료");
                    getTenantUsersAll(selectedGroup.key, 0, listSize, '', '');
                })
                .catch((e) => {
                    showDialog(DialogEnum.DEFAULT, "오류", "테넌트 유저를 탈퇴 중 오류가 발생했습니다.");
                });
        });
    };


    // 서비스 상세정보 모달 핸들러
    const handleDetailInfo = (data) => {
        console.log("[TenantUser] detail open", {
            selectedGroupKey: selectedGroup?.key,
            selectedTenant,
            selectedGroupPermission: selectedTenant?.currentUserPermission,
            currentUser,
            canManageTenantUser,
            rowData: data,
        });

        const modalContent = <TenantUserDetailModal data={data} canManageTenantUser={canManageTenantUser} update={() => getTenantUsersAll(selectedGroup.key, 0, listSize, '', '')}
            onDelete={handleWithdrawal}
        />
        const modalOption = { modalTitle: '테넌트 유저 정보' }
        openModal(<DetailModal content={modalContent} option={modalOption} />)
    };

    return (
        <div className={styles.layoutWrapper}>
            <h3>테넌트 유저 관리</h3>
            <div className={styles.contentsWrapper}>
                {showGroupTree && (
                    <div className={styles.sidebar}
                        style={{
                            marginRight: "5px",
                            width: !isMobileWindow ? "250px" : "100px",
                            minWidth: !isMobileWindow ? "200px" : "100px"
                        }}>
                        <div className={styles.groupList}>
                            {treeData.map((group) => (
                                <InnocodeList
                                    key={group.key}
                                    className={`${styles.groupItem} ${selectedGroup?.key === group.key ? styles.selected : ''}`}
                                    onClick={() => handleGroupSelect(group)}
                                >
                                    <span className={styles.groupName}>{group.title}</span>
                                </InnocodeList>
                            ))}
                        </div>
                    </div>
                )}
                <div className={styles.contentBox}>
                    <div className={styles.permissionPanel}>
                        <TenantsUserTable
                            datas={cellData}
                            context={context}
                            detailUser={handleDetailInfo}
                            onAddUser={handleRegisterUser}
                            canInvite={canManageTenantUser}
                            onDeleteUsers={handleWithdrawal}
                            onPrevPage={handlePrevPage}
                            onNextPage={handleNextPage}
                            disablePrev={page === 0}
                            disableNext={(page + 1) * listSize >= totalCount}
                            pageNumber={page}
                            totalCount={totalCount}
                            listSize={listSize}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                            onSearch={handleSearch}
                            isMobileWindow={isMobileWindow}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TenantUser;
